import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import GPSelect from '@gp/components/Select';
import GPSelectMultiple from '@gp/components/SelectMultiple';
import {
  getArraySort,
  toFirstUpperCase,
} from '@gp/utils/common';
import {
  fetchStudioOverallOverviewPageData,
  fetchStudioOverallProjectsLiveFilterData,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import { useRequest, useUpdateEffect } from 'ahooks';
import { sourceOptionTextRenderer } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Projects/renderers';
// import './index.scss';

interface FilterPropsType {
  onChange: (payload: {
    source: string,
    bo: string[],
    genre: string[],
    genreMap: any[],
    project: string[],
    projectMap: any[],
  }) => void,
  setFilterLoading: React.Dispatch<React.SetStateAction<boolean>> | null,
}

const DEFAULT_PROPS: FilterPropsType = {
  onChange: () => {},
  setFilterLoading: null,
};

function Filter(props: FilterPropsType) {

  const {
    onChange,
    setFilterLoading,
  } = props;

  const [sourceValue, setSourceValue] = useState<string>('internal');
  // const [isInit, setIsInit] = useState<boolean>(true);
  const [selectBO, setSelectBO] = useState<string[]>([]);
  const [lastSelectBO, setLastSelectBO] = useState<string[]>([]);
  const [selectGenre, setSelectGenre] = useState<string[]>([]);
  const [lastSelectGenre, setLastSelectGenre] = useState<string[]>([]);
  const [selectProject, setSelectProject] = useState<string[]>([]);
  const [lastSelectProject, setLastSelectProject] = useState<string[]>([]);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [selectProjectMapList, setSelectProjectMapList] = useState<any[]>([]); // 选中的project对应的详细信息
  const [genreList, setGenreList] = useState<any[]>([]);
  const [selectGenreMapList, setSelectGenreMapList] = useState<any[]>([]); // 选中的genre对应的详细信息

  // 筛选器数据
  const {
    loading: filterLoading,
    data: filterList,
    run: filterRun,
  } = useRequest(
    fetchStudioOverallProjectsLiveFilterData,
    {manual: true},
  );
  const {
    loading: boFilterLoading,
    data: boFilterList,
  } = useRequest(
    fetchStudioOverallOverviewPageData,
    {defaultParams: [{page: 'projects'}]},
  );

  const loading = useMemo(() => {
    const res = boFilterLoading || filterLoading;
    setFilterLoading?.(res);
    return res;
  }, [boFilterLoading, filterLoading]);

  const boOptions = useMemo(() => {
    const options = [];
    const boList = boFilterList?.management_team;
    if (Array.isArray(boList) && boList?.length) {
      // 先排序
      let isIncludeOthersItem = false;
      const filterOthersBOlist = boList?.filter((t: string) => {
        if (t?.toLowerCase() === 'others') {
          isIncludeOthersItem = true;
        }
        return t?.toLowerCase() !== 'others';
      });
      const tempSortBOList = getArraySort(filterOthersBOlist, 'string', 'asc');
      const sortBOList = isIncludeOthersItem ? [...tempSortBOList, 'Others'] : tempSortBOList;
      for(let i = 0; i < sortBOList?.length; i ++) {
        const t = sortBOList?.[i];
        options.push({
          text: t,
          value: t,
          groupKey: 'separated',
        });
      }
    }

    return options;
  }, [boFilterLoading]);

  const projectOptions = useMemo(() => {
    const projectList = filterList?.list;
    if (!Array.isArray(projectList) || !projectList?.length) {
      return [];
    }
    setProjectList(projectList);
    const _options = getArraySort(projectList, 'string', 'asc', 'name')?.map((t: {id: string, name: string}) => {
      return {
        value: t?.id || '',
        text: t?.name || '-',
        label: t?.name || '-',
        groupKey: 'separated',
      };
    }) || [];

    return _options;
  }, [filterLoading]);

  const genreOptions = useMemo(() => {
    const genreList = filterList?.genre;
    if (!genreList || Object.prototype.toString.call(genreList) !== '[object Object]') {
      return [];
    }
    // 找出Others项，并把数据转换成数组
    const filterOthersGenreList = [];
    let othersItem = null;
    for (const key in genreList) {
      if (!key) {
        othersItem = genreList[key];
      } else {
        filterOthersGenreList.push({
          name: key,
          msg: genreList[key],
        });
      }
    }
    setGenreList(othersItem ? [...filterOthersGenreList, {name: 'Others', msg: othersItem}] : filterOthersGenreList);
    const _options = getArraySort(filterOthersGenreList, 'string', 'asc', 'name')?.map((t: {name: string, msg: string}) => {
      return {
        value: t?.name || '',
        text: t?.name || '-',
        label: t?.name || '-',
        groupKey: 'separated',
      };
    }) || [];
    if (othersItem) _options.push({
        value: 'Others',
        text: 'Others',
        label: 'Others',
        groupKey: 'separated',
    });

    return _options;
  }, [filterLoading]);

  const handleSourceFilterChange = useCallback((v: string) => {
    setSourceValue(v);
    reset(v);
    /* onChange({
      source: v,
      bo: selectBO,
      genre: selectGenre,
      genreMap: selectGenreMapList,
      project: selectProject,
      projectMap: selectProjectMapList,
    }); */
  }, [/* selectBO, selectGenre, selectGenreMapList, selectProject, selectProjectMapList */]);

  const handleBOFilterChange = useCallback((v: string[]) => {
    // 重复全选的效果不请求接口
    if (v?.length === boFilterList?.management_team?.length && !lastSelectBO?.length) return;
    // 当length是全选时，也设置成[]
    if (v?.length === boFilterList?.management_team?.length) {
      setSelectBO([]);
      setLastSelectBO([]);
      onChange({
        source: sourceValue,
        bo: [],
        genre: selectGenre,
        genreMap: selectGenreMapList,
        project: selectProject,
        projectMap: selectProjectMapList,
      });
      return;
    }

    const newSelectBO = v ? [...v] : [];
    setSelectBO(newSelectBO);
    setLastSelectBO(newSelectBO);
    onChange({
      source: sourceValue,
      bo: newSelectBO,
      genre: selectGenre,
      genreMap: selectGenreMapList,
      project: selectProject,
      projectMap: selectProjectMapList,
    });
  }, [boFilterList?.management_team?.length, lastSelectBO, sourceValue, selectGenre, selectGenreMapList, selectProject, selectProjectMapList]);

  const handleGenreFilterChange = useCallback((v: string[]) => {
    // 重复全选的效果不请求接口
    if (v?.length === genreList?.length && !lastSelectGenre?.length) return;
    // 当length是全选时，也设置成[]
    if (v?.length === genreList?.length) {
      setSelectGenre([]);
      setSelectGenreMapList([]);
      setLastSelectGenre([]);
      onChange({
        source: sourceValue,
        bo: selectBO,
        genre: [],
        genreMap: [],
        project: selectProject,
        projectMap: selectProjectMapList,
      });
      return;
    }

    const newSelectGenre = v ? [...v] : [];
    const newSelectGenreMapList = genreList?.filter((t) => (newSelectGenre?.includes(t?.name)));
    setSelectGenre(newSelectGenre);
    setSelectGenreMapList(newSelectGenreMapList);
    setLastSelectGenre(newSelectGenre);
    onChange({
      source: sourceValue,
      bo: selectBO,
      genre: newSelectGenre,
      genreMap: newSelectGenreMapList,
      project: selectProject,
      projectMap: selectProjectMapList,
    });
  }, [genreList?.length, lastSelectGenre, sourceValue, selectBO, selectProject, selectProjectMapList]);

  const handleProjectFilterChange = useCallback((v: string[]) => {
    // 重复全选的效果不请求接口
    if (v?.length === projectList?.length && !lastSelectProject?.length) return;
    // 当length是全选时，也设置成[]
    if (v?.length === projectList?.length) {
      setSelectProject([]);
      setSelectProjectMapList([]);
      setLastSelectProject([]);
      onChange({
        source: sourceValue,
        bo: selectBO,
        genre: selectGenre,
        genreMap: selectGenreMapList,
        project: [],
        projectMap: [],
      });
      return;
    }

    const newSelectProject = v ? [...v] : [];
    const newSelectProjectMapList = projectList?.filter((t) => (newSelectProject?.includes(t?.id)));
    setSelectProject(newSelectProject);
    setSelectProjectMapList(newSelectProjectMapList);
    setLastSelectProject(newSelectProject);
    onChange({
      source: sourceValue,
      bo: selectBO,
      genre: selectGenre,
      genreMap: selectGenreMapList,
      project: newSelectProject,
      projectMap: newSelectProjectMapList,
    });
  }, [projectList?.length, lastSelectProject, sourceValue, selectBO, selectGenre, selectGenreMapList]);

  const reset = useCallback((sourceValue: string) => {
    setSelectBO([]);
    setLastSelectBO([]);
    setSelectGenre([]);
    setSelectGenreMapList([]);
    setLastSelectGenre([]);
    setSelectProject([]);
    setSelectProjectMapList([]);
    setLastSelectProject([]);
    onChange({
      source: sourceValue,
      bo: [],
      genre: [],
      genreMap: [],
      project: [],
      projectMap: [],
    });
  }, [sourceValue]);

  /* useUpdateEffect(() => {
    if (!filterLoading) {
      if (isInit) {
        setIsInit(false);
        return;
      }
      reset();
    }
  }, [filterLoading]); */

  // 获取筛选器数据
  useEffect(() => {
    if (sourceValue === 'internal') {
      filterRun({
        internal: true,
      });
    } else {
      filterRun();
    }
  }, [sourceValue]);

  return (
    <>
      <GPSelect
        label="Source"
        style={{ marginRight: 10 }}
        disabled={loading}
        value={sourceValue}
        optionTextRenderer={sourceOptionTextRenderer}
        options={[
          {
            text: 'Internal',
            value: 'internal',
          },
          {
            text: 'Intelligence',
            value: 'intelligence',
          },
        ]}
        button={sourceValue ? (
          <>
            <span className="select-button-text">Source</span>
            <span className="select-button-value">{toFirstUpperCase(sourceValue)}</span>
          </>
        ) : undefined}
        onChange={handleSourceFilterChange}
        className="dbg-studio-overall-projects-live-source-selector"
      />
      <GPSelectMultiple
        className="dbg-studio-overall-projects-live-bo-selector"
        style={{marginRight: 10}}
        label="Business Owner"
        searchable
        disabled={loading}
        options={boOptions}
        value={selectBO}
        onChange={handleBOFilterChange}
      />
      <GPSelectMultiple
        className="dbg-studio-overall-projects-live-genre-selector"
        style={{marginRight: 10}}
        label="Genre"
        searchable
        disabled={loading}
        options={genreOptions}
        value={selectGenre}
        onChange={handleGenreFilterChange}
      />
      <GPSelectMultiple
        className="dbg-studio-overall-projects-live-project-selector"
        label="Project"
        searchable
        disabled={loading}
        options={projectOptions}
        value={selectProject}
        onChange={handleProjectFilterChange}
      />
    </>
  );
}

Filter.defaultProps = DEFAULT_PROPS;

export default memo(Filter);
